/**
 * Floating feedback bubble (trial-week feedback feature). Mounted once in the
 * authenticated shell, so it rides along on every page (never on login/auth —
 * those render outside AppShell). Joey writes a free-form note; the client
 * auto-captures the route, parsed entity IDs, and viewport. Draft text survives
 * navigation via localStorage. Cmd/Ctrl+/ opens it from anywhere.
 *
 * Design: monochrome, no shadow, no badge — per the S1J Mail design system.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import type { FeedbackNote, SettingsPco } from '@coffman-tm/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useToast } from './ToastProvider';
import { getPageContextIds, getPageLabel } from '../lib/feedback-page';

const NOTE_MIN = 10;

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.round(diff / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function FeedbackBubble({ pcos }: { pcos: SettingsPco[] }) {
  const { token, me } = useAuth();
  const location = useLocation();
  const toast = useToast();
  const userId = me?.user.id ?? 'anon';
  const draftKey = `coffman-tm:feedback-draft:${userId}`;

  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mine, setMine] = useState<FeedbackNote[]>([]);
  const [showPrev, setShowPrev] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Resolve the PCO code for /pcos/:pcoId routes from the nav's PCO list (no
  // extra request). Falls back to a short id inside getPageLabel.
  const pcoId = getPageContextIds(location.pathname).pco_id;
  const pcoCode = pcoId ? (pcos.find((p) => p.id === pcoId)?.pco_number ?? null) : null;
  const pageLabel = getPageLabel(location.pathname, { pcoCode });

  // Load the draft once on mount.
  useEffect(() => {
    const saved = localStorage.getItem(draftKey);
    if (saved) setText(saved);
  }, [draftKey]);

  // Persist the draft as it changes.
  useEffect(() => {
    if (text) localStorage.setItem(draftKey, text);
    else localStorage.removeItem(draftKey);
  }, [text, draftKey]);

  const loadMine = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.feedback.mine(token);
      setMine(res.notes);
    } catch {
      /* non-blocking */
    }
  }, [token]);

  // Open helper: focus the textarea, refresh the previous-notes list.
  const openPanel = useCallback(() => {
    setOpen(true);
    void loadMine();
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [loadMine]);

  // Cmd/Ctrl+/ toggles open from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        openPanel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPanel]);

  const trimmed = text.trim();
  const canSubmit = trimmed.length >= NOTE_MIN && !busy;

  const handleSubmit = async () => {
    if (!token || !canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await api.feedback.create(token, {
        note_text: trimmed,
        page_route: location.pathname,
        page_label: pageLabel,
        page_context_json: getPageContextIds(location.pathname) as Record<string, string>,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
      });
      setText('');
      localStorage.removeItem(draftKey);
      toast.push({ variant: 'success', title: 'Note saved', body: 'The support team will see it.' });
      await loadMine();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save note');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        aria-label="Leave feedback"
        title="Leave a note (Cmd/Ctrl + /)"
        onClick={openPanel}
        style={bubbleStyle}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--paper-3)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--surface)')}
      >
        <MessageSquare size={18} strokeWidth={1.75} color="var(--ink-3)" />
      </button>
    );
  }

  return (
    <div style={panelStyle} role="dialog" aria-label="Leave a note">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>Leave a note</span>
        <button
          type="button"
          aria-label="Close"
          onClick={() => setOpen(false)}
          style={iconBtnStyle}
        >
          <X size={16} strokeWidth={2} color="var(--ink-3)" />
        </button>
      </div>

      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
        On: <span style={{ color: 'var(--ink-2)' }}>{pageLabel}</span>
      </div>

      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={6}
        placeholder="What did you notice? Bugs, ideas, questions — anything."
        style={textareaStyle}
        disabled={busy}
      />

      {err && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}

      <button
        type="button"
        className="btn btn-cta"
        onClick={handleSubmit}
        disabled={!canSubmit}
        style={{ width: '100%', justifyContent: 'center' }}
      >
        {busy ? 'Saving…' : 'Send note'}
      </button>

      <div style={{ marginTop: 12, borderTop: '1px solid var(--rule)', paddingTop: 10 }}>
        <button
          type="button"
          className="btn-link"
          style={{ fontSize: 12.5 }}
          onClick={() => setShowPrev((v) => !v)}
        >
          {showPrev ? '▾' : '▸'} Your previous notes ({mine.length})
        </button>
        {showPrev && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mine.length === 0 ? (
              <div className="muted" style={{ fontSize: 12.5 }}>No notes yet.</div>
            ) : (
              mine.slice(0, 20).map((n) => (
                <div key={n.id} style={{ fontSize: 12.5, lineHeight: 1.4 }}>
                  <div style={{ color: 'var(--ink-3)' }}>
                    {relTime(n.created_at)} · {n.page_label.slice(0, 40)}
                  </div>
                  <div style={{ color: 'var(--ink-2)' }}>
                    {n.note_text.slice(0, 60)}
                    {n.note_text.length > 60 ? '…' : ''}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const bubbleStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  width: 40,
  height: 40,
  borderRadius: '50%',
  border: '1px solid var(--rule)',
  background: 'var(--surface)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 50,
  padding: 0,
};

const panelStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  width: 360,
  maxHeight: '70vh',
  overflowY: 'auto',
  background: 'var(--surface)',
  border: '1px solid var(--rule)',
  borderRadius: 8,
  padding: 16,
  zIndex: 50,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  fontSize: 13,
  fontWeight: 400,
  fontFamily: 'inherit',
  padding: '8px 10px',
  border: '1px solid var(--rule)',
  borderRadius: 6,
  background: 'var(--paper)',
  color: 'var(--ink)',
  resize: 'vertical',
};

const iconBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
};
